<template>
    <div :class="inputClass" class="zs-dropdown-container" v-click-outside="closeDropdown" @keydown.esc="closeDropdown"
        @keydown.down.prevent="handleDownKey" @keydown.up.prevent="handleUpKey" @keydown.enter.prevent="handleEnterKey">
        <div class="zs-dropdown-toggle" @click="toggleDropdown">
            <ZSInput :disabled="disabled" :type="showDropdownIcon?'dropdown':''" :clear="clear" @clear="reset" :busy="isBusy"
                :label="label" v-model="internalValue" :valid="!hasError" :toValidate="false" @input="onInput"
                :placeholder="computedPlaceholder" @blur="onInputBlur" :error="errorMessage" 
                :size="size" :errorType="errorType" :errorBadgePosition="errorBadgePosition" :errorBadgeOnHover="errorBadgeOnHover" ref="zsInput">
            </ZSInput>
        </div>
        <div v-if="isOpen" ref="zsMenu" :class="{ 'zs-menu-top': isAbove, 'no-label': !label }" class="zs-dropdown-menu"
            :style="{ maxHeight: maxHeight }">
            <div v-for="(item, index) in internalItems" :key="index"
                :class="['zs-dropdown-item', { 'active': index === activeItemIndex }, size]" @click="selectItem(item)">
                <slot :item="item">
                    <span style="font-size: 80%;">{{ item[itemLabel] ||  item }}</span>
                </slot>
            </div>
        </div>
    </div>
</template>

<script setup>

import { ref, computed, watch, onMounted, onBeforeUnmount, inject, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import ZSInput from './ZSInput.vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    modelValue: { type: [String, Number, Object], default: null },
    items: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    maxHeight: { type: String, default: '200px' },
    label: { type: String, default: '' },
    rules: { type: String, default: '' },
    value: { type: [String, Number, Object], default: null },
    itemLabel: { type: String, default: 'label' },
    itemValue: { type: String, default: null },
    busy: { type: Boolean, default: false },
    loadFunction: { type: Function, default: null },
    delay: { type: Number, default: 300 },
    clear: { type: Boolean, default: false },
    resetValue: { type: [String, Number, Object], default: null },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: 'small' },
    inputClass: { type: String, default: '' },
    showDropdownIcon: { type: Boolean, default: true },
    valid: { type: [Boolean, String], default: true },
    toValidate: { type: Boolean, default: true },
    validateOnChange: { type: Boolean, default: true },
    error: { type: String, default: null },
    errorType: { type: String, default: 'badge' },
    errorBadgePosition: { type: String, default: 'br' },
    errorBadgeOnHover: { type: Boolean, default: false },
    field: { type: String, default: '' },
    autoSelectFirst: { type: Boolean, default: false },
  id: {
    type: String,
    default: () => `zs-select-${Math.random().toString(36).substr(2, 9)}`
  },    
});

const emit = defineEmits(['input', 'item-selected', 'change', 'update:modelValue', 'blur', 'validation']);

const { t } = useI18n();
const validator = inject('validator', null);
const zsInput = ref(null);
const zsMenu = ref(null);
const isOpen = ref(false);
const isBusy = ref(props.busy);
const isAbove = ref(false);
const validationResult = ref({});
const internalItems = ref([]);
const internalValue = ref('');
const selectedItem = ref(null);
const activeItemIndex = ref(-1);
const internalError = ref('');

const computedPlaceholder = computed(() => {
    return props.placeholder || t('components.select_item');
});

const hasError = computed(() => {
    return validationResult.value && validationResult.value.description || props.error || internalError.value
});

const errorMessage = computed(() => {
    if (!hasError.value) return null;
    return validationResult.value?.description || props.error || internalError.value || null;
});

const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
};

const loadFn = props.loadFunction ? debounce(loadItems, props.delay) : null;

function setSelection(item) {
    selectedItem.value = item;
    if (item) {
        internalValue.value = typeof item === 'object' ? item[props.itemLabel] : item;
        const valueToEmit = props.itemValue && typeof item === 'object' ? item[props.itemValue] : item;
        
        if (props.modelValue !== valueToEmit) {
            emit('input', valueToEmit);
            emit('update:modelValue', valueToEmit);
            emit('item-selected', item);
        }
    } else {
        // reset
        selectedItem.value = null;
        internalValue.value = '';
        if (props.modelValue !== props.resetValue) {
            emit('input', props.resetValue);
            emit('update:modelValue', props.resetValue);
        }
    }

    if (props.validateOnChange) {
        validate();
    }
}

watch([() => props.modelValue, () => props.items], ([newValue, newItems]) => {
    internalItems.value = newItems || [];
    let foundItem = null;

    if (newValue !== null && newValue !== undefined && newValue !== props.resetValue) {
        if (props.itemValue) {
            foundItem = internalItems.value.find(item => item && item[props.itemValue] === newValue);
        } else {
            // For primitives or object equality
            foundItem = internalItems.value.find(item => item === newValue);
        }
    }

    if (foundItem) {
        selectedItem.value = foundItem;
        internalValue.value = typeof foundItem === 'object' ? foundItem[props.itemLabel] : String(foundItem);
    } else if (props.autoSelectFirst && internalItems.value.length > 0 && (newValue === null || newValue === undefined || newValue === props.resetValue)) {
        setSelection(internalItems.value[0]);
    } else {
        selectedItem.value = null;
       if(!loadFn) internalValue.value = '';
       
    }

}, { deep: true, immediate: true });


watch(() => props.rules, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        validate();
    }
});

function openDropdown() {
    if (props.disabled) return;
    isOpen.value = true;
    nextTick(() => {
        positionMenu();
        // Find and scroll to the currently selected item
        scrollToSelectedItem();
    });
}

function toggleDropdown() {
    if (props.disabled) return;
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        nextTick(() => {
            positionMenu();
            // Find and scroll to the currently selected item
            scrollToSelectedItem();
        });
    }
}

function closeDropdown() {
    internalItems.value = props.items;
    isOpen.value = false;
    activeItemIndex.value = -1;
}

function selectItem(item) {
    setSelection(item);
    closeDropdown();
}

function onInput(val) {
    internalValue.value = val;
    isOpen.value = true;
    emit('change', val);
    if (loadFn) {
        internalItems.value = [];
        if ((val?.trim())?.length) {
            isBusy.value = true;
            loadFn(val);
        }
    } else if (val.length > 0) {
        internalItems.value = props.items.filter((item) =>
            item[props.itemLabel].toLowerCase().includes(val.toLowerCase())
        );
    } else {
        internalItems.value = props.items;
        reset();
        if (props.validateOnChange) validate();
    }
}

function loadItems(search) {
    props.loadFunction(search).then((items) => {
        internalItems.value = items;
        isBusy.value = false;
    });
}

function validateData(value) {
    if (validator && props.rules !== '') {
        validationResult.value = validator.validate({
            id: props.id || `zs-select-${Math.random().toString(36).substr(2, 9)}`,
            value: value,
            rules: props.rules,
            field: props.field || props.label,
            internalError: internalError.value
        });

        emit('validation', validationResult.value);
        return validationResult.value;
    }
    return null;
}

function validate() {
    const toValidate = props.itemValue
        ? (selectedItem.value?.[props.itemValue] ?? '')
        : selectedItem.value;

    return validateData(toValidate);
}

const reset = () => {
    selectedItem.value = null;
    internalValue.value = '';
    validationResult.value = {};
    internalError.value = '';
    if(loadFn) internalItems.value = [];
    emit('input', props.resetValue);
    emit('update:modelValue', props.resetValue);
};

const setError = (errorMessage) => {
    internalError.value = errorMessage;
};

const clearError = () => {
    internalError.value = '';
};

const subscriber = { validate, reset };

const subscribeToValidator = () => {
    if (validator) {
        validator.subscribe(subscriber);
    }
};

function positionMenu() {
    if (!isOpen.value) return;
    const inputRect = zsInput.value.getRef().getBoundingClientRect();
    const menuRect = zsMenu.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (inputRect.bottom + menuRect.height <= viewportHeight) {
        isAbove.value = false;
    } else if (inputRect.top - menuRect.height >= 0) {
        isAbove.value = true;
    } else {
        isAbove.value = (inputRect.top > viewportHeight - inputRect.bottom);
    }

    const viewportWidth = window.innerWidth;
    if (inputRect.left + menuRect.width > viewportWidth) {
        zsMenu.value.style.right = '0';
        zsMenu.value.style.left = 'auto';
    }
}

function handleDownKey() {
    if (!isOpen.value) {
        openDropdown();
        activeItemIndex.value = 0;
    } else if (internalItems.value.length > 0) {
        activeItemIndex.value = (activeItemIndex.value + 1) % internalItems.value.length;
        scrollToActiveItem();
    }
}

function handleUpKey() {
    if (isOpen.value && internalItems.value.length > 0) {
        activeItemIndex.value = activeItemIndex.value <= 0
            ? internalItems.value.length - 1
            : activeItemIndex.value - 1;
        scrollToActiveItem();
    }
}

function handleEnterKey() {
    if (isOpen.value && activeItemIndex.value >= 0 && activeItemIndex.value < internalItems.value.length) {
        selectItem(internalItems.value[activeItemIndex.value]);
    } else if (!isOpen.value) {
        openDropdown();
    }
}

function scrollToActiveItem() {
    nextTick(() => {
        if (!zsMenu.value) return;

        const menuItems = zsMenu.value.querySelectorAll('.zs-dropdown-item');
        if (activeItemIndex.value >= 0 && menuItems[activeItemIndex.value]) {
            const activeItem = menuItems[activeItemIndex.value];
            const menuContainer = zsMenu.value;

            const itemTop = activeItem.offsetTop;
            const itemHeight = activeItem.clientHeight;
            const menuHeight = menuContainer.clientHeight;
            const scrollTop = menuContainer.scrollTop;

            if (itemTop < scrollTop) {
                // Scroll up to show the active item
                menuContainer.scrollTop = itemTop;
            } else if (itemTop + itemHeight > scrollTop + menuHeight) {
                // Scroll down to show the active item
                menuContainer.scrollTop = itemTop + itemHeight - menuHeight;
            }
        }
    });
}

function scrollToSelectedItem() {
    nextTick(() => {
        if (!zsMenu.value || !selectedItem.value) return;

        // Find the index of the currently selected item
        let selectedIndex = -1;
        if (props.itemValue) {
            selectedIndex = internalItems.value.findIndex(item => 
                item && item[props.itemValue] === selectedItem.value[props.itemValue]
            );
        } else {
            selectedIndex = internalItems.value.findIndex(item => item === selectedItem.value);
        }

        if (selectedIndex >= 0) {
            // Set the active item index to the selected item
            activeItemIndex.value = selectedIndex;
            
            const menuItems = zsMenu.value.querySelectorAll('.zs-dropdown-item');
            if (menuItems[selectedIndex]) {
                const selectedItemElement = menuItems[selectedIndex];
                const menuContainer = zsMenu.value;

                const itemTop = selectedItemElement.offsetTop;
                const itemHeight = selectedItemElement.clientHeight;
                const menuHeight = menuContainer.clientHeight;
                const scrollTop = menuContainer.scrollTop;

                if (itemTop < scrollTop) {
                    // Scroll up to show the selected item
                    menuContainer.scrollTop = itemTop;
                } else if (itemTop + itemHeight > scrollTop + menuHeight) {
                    // Scroll down to show the selected item
                    menuContainer.scrollTop = itemTop + itemHeight - menuHeight;
                }
            }
        } else {
            // Reset active item index if no selected item found
            activeItemIndex.value = -1;
        }
    });
}

const onInputBlur = () =>{
    // nextTick(() => emit('blur',internalValue.value+"..." ))
} 

onMounted(() => {
    if (props.toValidate) subscribeToValidator();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu);
});

onBeforeUnmount(() => {
    if (validator) {
        const index = validator.subscribers.indexOf(subscriber);
        if (index > -1) validator.subscribers.splice(index, 1);
    }
    window.removeEventListener('resize', positionMenu);
    window.removeEventListener('scroll', positionMenu);
});

defineExpose({
    validate,
    reset,
    setError,
    clearError
});
</script>

<style scoped>
.zs-dropdown-container {
    --zs-dropdown-container-position: relative;
    --zs-dropdown-toggle-padding: 0;
    /* Assuming no padding by default, adjust if needed */
    --zs-dropdown-toggle-cursor: pointer;
    --zs-dropdown-menu-position: absolute;
    --zs-dropdown-menu-top: 100%;
    --zs-dropdown-menu-left: 0;
    --zs-dropdown-menu-width: 100%;
    --zs-dropdown-menu-bg-color: white;
    --zs-dropdown-menu-border: 1px solid #ccc;
    --zs-dropdown-menu-overflow-y: auto;
    --zs-dropdown-menu-z-index: 1000;
    --zs-dropdown-menu-border-radius-bottom: 0 0 4px 4px;
    --zs-dropdown-menu-top-bottom: 100%;
    --zs-dropdown-menu-top-border-radius: 4px 4px 0 0;
    --zs-dropdown-menu-top-no-label-bottom: 100%;
    --zs-dropdown-item-padding: 8px 12px;
    --zs-dropdown-item-cursor: pointer;
    --zs-dropdown-item-border-top: 1px solid #eee;
    --zs-dropdown-item-small-padding: 4px 8px;
    --zs-dropdown-item-small-font-size: 0.875em;
    --zs-dropdown-item-large-padding: 12px 16px;
    --zs-dropdown-item-large-font-size: 1.125em;
    --zs-dropdown-item-hover-bg-color: #f0f0f0;

    position: var(--zs-dropdown-container-position);
    /* width: 100%; */
}

.full-width,
.block {
    width: 100%;
}

.zs-dropdown-toggle {
    padding: var(--zs-dropdown-toggle-padding);
    cursor: var(--zs-dropdown-toggle-cursor);
}

.zs-dropdown-menu {
    position: var(--zs-dropdown-menu-position);
    top: var(--zs-dropdown-menu-top);
    left: var(--zs-dropdown-menu-left);
    width: var(--zs-dropdown-menu-width);
    background-color: var(--zs-dropdown-menu-bg-color);
    border: var(--zs-dropdown-menu-border);
    overflow-y: var(--zs-dropdown-menu-overflow-y);
    z-index: var(--zs-dropdown-menu-z-index);
    border-top: none;
    border-radius: var(--zs-dropdown-menu-border-radius-bottom);

    &.zs-menu-top {
        top: auto;
        bottom: var(--zs-dropdown-menu-top-bottom);
        border-radius: var(--zs-dropdown-menu-top-border-radius);

        &.no-label {
            bottom: var(--zs-dropdown-menu-top-no-label-bottom);
        }
    }
}

.zs-dropdown-item {
    padding: var(--zs-dropdown-item-padding);
    cursor: var(--zs-dropdown-item-cursor);
    border-top: var(--zs-dropdown-item-border-top);
}

.zs-dropdown-item.small {
    padding: var(--zs-dropdown-item-small-padding);
    font-size: var(--zs-dropdown-item-small-font-size);
}

.zs-dropdown-item.large {
    padding: var(--zs-dropdown-item-large-padding);
    font-size: var(--zs-dropdown-item-large-font-size);
}

.zs-dropdown-item:hover,
.zs-dropdown-item.active {
    background-color: var(--zs-dropdown-item-hover-bg-color);
}
</style>